import { store } from 'store'
import { deleteRedirect, resetSalesSurvey, resetUrlSearchParams } from 'store/app/slice'
import { switchLanguage as switchLanguageAction } from 'store/app/thunks'
import { clearSessionTokens, clearTokens, isAuthenticated } from 'utils/localStorageService'
import { UserTypes } from 'store/auth/types'
import { checkIsSales } from 'utils/sales'
import { routes } from 'types/routes'
import { getSessionUserType, getUserType } from 'utils/userType'
import { USER_TYPE_CONFIG } from 'constants/userTypes'
import { clearUser as clearUserAction } from 'store/contact/slice'
import { KNOWN_DOWNTIME_PERIODS } from 'constants/downtime.ts'
import dayjs from 'dayjs'
import { CheckIsDown, SystemDownTypes } from 'types/downtime.ts'
import { IS_PROD_OR_BETA, SYSTEM_DOWN } from 'constants/envs.ts'
import i18next from 'i18next'
import isBetween from 'dayjs/plugin/isBetween'
import { MOBILE_BREAKPOINT, TABLET_BREAKPOINT } from 'constants/viewport.ts'
import { boltApi } from 'store/queries/bolt-api'
import { clearSalesStore } from 'store/sales/slice.ts'
import { resetAuth } from 'store/auth/slice'
import posthog from 'posthog-js'
import { trackEvent } from 'utils/tracking.ts'
import { Language } from '@boltenergy-be/shared'

dayjs.extend(isBetween)

/**
 * Handles the clear user action
 */
export const clearUser = () => {
  // Remove redux state persistence
  sessionStorage.removeItem('persist:contact')

  // Clear redux states
  store.dispatch(clearUserAction())
  store.dispatch(clearSalesStore())
  store.dispatch(resetUrlSearchParams())

  // Reset RTK Query cache
  store.dispatch(boltApi.util.resetApiState())
}

/**
 * Helper function to get the value of a CSS variable
 * @param {string} name
 * @param {string} fallback
 * @returns {string}
 */
export const getCssVariable = (name: string, fallback?: string): string =>
  getComputedStyle(document.documentElement).getPropertyValue(name) || fallback || ''

/**
 * Returns the correct refreshToken URL for the given userType
 *
 * @param {UserTypes} userType
 * @returns {string}
 */
export const getRefreshTokenUrlForUserType = (userType: UserTypes): string => {
  switch (userType) {
    case UserTypes.SALES:
      return '/auth/sales/refresh-token'

    case UserTypes.SUPER_USER:
      return '/auth/internal/refresh-token'

    default:
      return '/auth/refresh-token'
  }
}

/**
 * Handles the logout action
 */
export const logout = (redirectFn: () => void, userType?: UserTypes) => {
  // remove redirect from AppStore if still set
  store.dispatch(deleteRedirect())
  clearTokens(userType || getUserType())
  clearUser()
  store.dispatch(resetAuth())
  store.dispatch(resetSalesSurvey())

  trackEvent({ category: 'auth', object: 'logout', action: 'click' }, { userType })
  posthog.reset()
  redirectFn()
}

/**
 * Ends a magic-link session. Magic-link tokens live in sessionStorage and have no refresh path, so on expiry the
 * session is terminated for this tab only: sessionStorage tokens are cleared while the localStorage login session
 * (and therefore other tabs) is left untouched.
 *
 * @param {() => void} redirectFn
 * @param {UserTypes} userType
 */
export const logoutMagicSession = (redirectFn: () => void, userType: UserTypes) => {
  store.dispatch(deleteRedirect())
  clearSessionTokens(userType)
  clearUser()
  store.dispatch(resetAuth())

  trackEvent({ category: 'auth', object: 'magic_session_logout', action: 'end' }, { userType })
  posthog.reset()
  redirectFn()
}

/**
 * Handles the switch language action
 */
export const switchLanguage = (language: Language) => {
  void store.dispatch(switchLanguageAction({ language }))
}

/**
 * Returns the correct redirect URL for the given userType
 * @param {UserTypes | null} userType
 * @returns {routes.CUSTOMER_EMAIL | routes.SALES_HOME | routes.ACCOUNT_SELECTION | routes.LOGIN_SALES | routes.LOGIN}
 */
export const getIndexRedirectUrl = (userType: UserTypes | null): routes => {
  const authenticatedUserTypes = isAuthenticated()
  const sessionsUserType = getSessionUserType()
  const userTypeToUse = userType || sessionsUserType
  const isSales = checkIsSales()

  // Get route for the given userType if it's authenticated
  if (userTypeToUse && authenticatedUserTypes[userTypeToUse]) {
    return USER_TYPE_CONFIG[userTypeToUse].home
  }

  if (Object.values(authenticatedUserTypes).some((isAuthenticated) => isAuthenticated)) {
    if (authenticatedUserTypes[UserTypes.SUPER_USER]) return USER_TYPE_CONFIG[UserTypes.SUPER_USER].home
    if (authenticatedUserTypes[UserTypes.SALES]) return USER_TYPE_CONFIG[UserTypes.SALES].home
    if (authenticatedUserTypes[UserTypes.CUSTOMER]) return USER_TYPE_CONFIG[UserTypes.CUSTOMER].home
  }

  return isSales ? USER_TYPE_CONFIG[UserTypes.SALES].login : USER_TYPE_CONFIG[UserTypes.CUSTOMER].login
}

/**
 * Checks if the app is down for maintenance
 * @param {UserTypes} loginPageUserType
 * @returns {CheckIsDown}
 */
export const checkIsDown = (loginPageUserType?: UserTypes): CheckIsDown => {
  const today = new Date()
  const warningPeriodStart = new Date().setDate(today.getDate() - 3)
  const userType = loginPageUserType || getUserType()
  const language = store.getState().app.language

  // Look for a scheduled downtime period
  const activePeriod = KNOWN_DOWNTIME_PERIODS.find((period) => dayjs(today).isBetween(period.start, period.end))
  const upcomingPeriod = KNOWN_DOWNTIME_PERIODS.find((period) => dayjs(today).isBetween(warningPeriodStart, period.start))

  // Check downtime states
  const isSystemDown = SYSTEM_DOWN && (!userType || userType === UserTypes.CUSTOMER)
  const isMaintenance = !!activePeriod && (!userType || activePeriod.userTypes.includes(userType))
  const isDown = isSystemDown || isMaintenance

  return {
    isDown: IS_PROD_OR_BETA && isDown,
    isApproaching: !!upcomingPeriod,
    message: isSystemDown
      ? i18next.t(($) => $.systemDown, {
          ns: 'common'
        })
      : activePeriod?.customMessage?.[language] ||
        upcomingPeriod?.customMessage?.[language] ||
        i18next.t(($) => $.downForMaintenance, {
          ns: 'common'
        }),
    type: isSystemDown ? SystemDownTypes.UNKNOWN : SystemDownTypes.MAINTENANCE
  }
}

/**
 * Check if the current viewport is equal or smaller than mobile
 * @param {number} width
 * @returns {boolean}
 */
export const checkIsMobile = (width?: number): boolean => {
  return (width ?? window?.innerWidth ?? 0) < MOBILE_BREAKPOINT
}

/**
 * Check if the current viewport is equal or smaller than tablet breakpoint
 * @param {number} width
 * @returns {boolean}
 */
export const checkIsTablet = (width?: number): boolean => {
  return (width ?? window?.innerWidth ?? 0) < TABLET_BREAKPOINT
}
